/* Keyboard & screen-reader plumbing for the modals and icon buttons:
   dialogs announce themselves, take focus when they open, trap Tab inside,
   close on Escape, and give focus back to where it was. */
import { $ } from './state.ts';

/** Modals by id; the first two run their own Enter/Escape handling. */
const MODALS = ['modal', 'cModal', 'helpModal', 'pasteModal', 'histModal', 'hsModal', 'authModal', 'shareModal'];
const SELF_KEYED = new Set(['modal', 'cModal']);
const FOCUSABLE = 'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

const focusables = (root: HTMLElement) => [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(e => e.offsetParent !== null);
const isOpen = (m: HTMLElement) => m.classList.contains('show');
const openModals = () => MODALS.map(id => document.getElementById(id)).filter((m): m is HTMLElement => !!m && isOpen(m));

export function installA11y(): void {
  const restore = new Map<HTMLElement, Element | null>();
  for (const id of MODALS) {
    const m = document.getElementById(id); if (!m) continue;
    const card = m.querySelector<HTMLElement>('.card') || m;
    card.setAttribute('role', 'dialog'); card.setAttribute('aria-modal', 'true');
    const h = card.querySelector('h2');
    if (h) { h.id ||= id + 'Title'; card.setAttribute('aria-labelledby', h.id); }
    else card.setAttribute('aria-label', id === 'cModal' ? 'Message' : 'Dialog');
    let wasOpen = isOpen(m);
    new MutationObserver(() => {
      const now = isOpen(m);
      if (now && !wasOpen) {
        restore.set(m, document.activeElement);
        // first field if there is one, else the primary button, else the close button
        const f = focusables(card);
        const target = f.find(e => e.matches('input, textarea')) || f.find(e => e.classList.contains('primary')) || f.find(e => !e.classList.contains('x')) || f[0];
        setTimeout(() => target?.focus(), 0);
      } else if (!now && wasOpen) {
        const back = restore.get(m) as HTMLElement | null;
        if (back && back.focus && document.contains(back)) back.focus();
        else (document.activeElement as HTMLElement | null)?.blur?.();
      }
      wasOpen = now;
    }).observe(m, { attributes: true, attributeFilter: ['class'] });
  }
  // Escape closes the topmost dialog; Tab stays inside it
  document.addEventListener('keydown', e => {
    const open = openModals();
    if (!open.length) return;
    const top = open[open.length - 1];
    if (e.key === 'Escape' && !SELF_KEYED.has(top.id)) { e.preventDefault(); e.stopPropagation(); top.classList.remove('show'); return; }
    if (e.key === 'Tab') {
      const f = focusables(top.querySelector<HTMLElement>('.card') || top);
      if (!f.length) return;
      const i = f.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && (i <= 0)) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && (i === -1 || i === f.length - 1)) { e.preventDefault(); f[0].focus(); }
    }
  }, true);
  // icon-only buttons: their tooltip is their name
  for (const b of document.querySelectorAll<HTMLElement>('button[title], a[title]'))
    if (!b.textContent?.trim() && !b.getAttribute('aria-label')) b.setAttribute('aria-label', b.title);
  const c = $('#c'); c.setAttribute('tabindex', '0'); c.setAttribute('aria-label', 'Floor plan. Pick a tool on the left; arrow keys nudge the selection, Delete removes it.');
}
